import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff, KeyRound, Loader, CheckCircle, AlertCircle } from 'lucide-react';
import { resetPassword } from '../services/api';
import './ResetPasswordPage.css';

const validate = (pw, confirm) => {
  if (pw.length < 8) return 'Passwort muss mindestens 8 Zeichen haben.';
  if (!/[A-Z]/.test(pw)) return 'Passwort muss einen Großbuchstaben enthalten.';
  if (!/[a-z]/.test(pw)) return 'Passwort muss einen Kleinbuchstaben enthalten.';
  if (!/[0-9]/.test(pw)) return 'Passwort muss eine Zahl enthalten.';
  if (!/[^A-Za-z0-9]/.test(pw)) return 'Passwort muss ein Sonderzeichen enthalten.';
  if (pw !== confirm) return 'Passwörter stimmen nicht überein.';
  return null;
};

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  if (!token) {
    return (
      <div className="rp-page">
        <div className="rp-card">
          <div className="rp-icon rp-icon--error"><AlertCircle size={26} /></div>
          <h1>Ungültiger Link</h1>
          <p>Kein Reset-Token gefunden. Bitte fordere einen neuen Link an.</p>
          <Link to="/" className="rp-btn rp-btn--primary">Zur Startseite</Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="rp-page">
        <div className="rp-card">
          <div className="rp-icon rp-icon--success"><CheckCircle size={26} /></div>
          <h1>Passwort geändert!</h1>
          <p>Dein Passwort wurde erfolgreich zurückgesetzt. Du kannst dich jetzt anmelden.</p>
          <Link to="/" className="rp-btn rp-btn--primary">Zur App &amp; Anmelden</Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate(password, confirm);
    if (err) { setError(err); return; }
    setError(null);
    setLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Der Link ist ungültig oder abgelaufen. Bitte fordere einen neuen an.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rp-page">
      <div className="rp-card">
        <div className="rp-icon rp-icon--blue"><KeyRound size={26} /></div>
        <h1>Neues Passwort</h1>
        <p>Wähle ein sicheres Passwort für dein LetsCareers-Konto.</p>

        {error && (
          <div className="rp-error">
            <AlertCircle size={15} /> {error}
          </div>
        )}

        <form className="rp-form" onSubmit={handleSubmit}>
          <div className="rp-field">
            <label>Neues Passwort</label>
            <div className="rp-input-wrap">
              <input
                type={showPw ? 'text' : 'password'}
                placeholder="Mindestens 8 Zeichen"
                value={password}
                onChange={e => { setPassword(e.target.value); setError(null); }}
                required
                disabled={loading}
                autoFocus
              />
              <button type="button" className="rp-eye" onClick={() => setShowPw(v => !v)}>
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div className="rp-field">
            <label>Passwort bestätigen</label>
            <div className="rp-input-wrap">
              <input
                type={showConfirm ? 'text' : 'password'}
                placeholder="Passwort wiederholen"
                value={confirm}
                onChange={e => { setConfirm(e.target.value); setError(null); }}
                required
                disabled={loading}
              />
              <button type="button" className="rp-eye" onClick={() => setShowConfirm(v => !v)}>
                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <ul className="rp-rules">
            <li className={password.length >= 8 ? 'ok' : ''}>Mindestens 8 Zeichen</li>
            <li className={/[A-Z]/.test(password) ? 'ok' : ''}>Ein Großbuchstabe</li>
            <li className={/[a-z]/.test(password) ? 'ok' : ''}>Ein Kleinbuchstabe</li>
            <li className={/[0-9]/.test(password) ? 'ok' : ''}>Eine Zahl</li>
            <li className={/[^A-Za-z0-9]/.test(password) ? 'ok' : ''}>Ein Sonderzeichen</li>
          </ul>

          <button type="submit" className="rp-btn rp-btn--primary rp-btn--submit" disabled={loading}>
            {loading ? <><Loader size={16} className="spin" /> Speichern...</> : 'Passwort speichern'}
          </button>
        </form>
      </div>
    </div>
  );
}
